// src/mobile_pages/contract/Cost.jsx

import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Maximize2 } from "lucide-react";
import MobileBoardPagination from "../../components/common/board/MobileBoardPagination";
import { getCosts } from "../../api/contract/cost";
import Cost_new from "./Cost_new";
import Cost_view from "./Cost_view";
import NivoBar from "../../components/common/graphs/NivoBar";
import { formatDate } from "../../utils/date";

const Cost = () => {
  const { id: projectId } = useParams();
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [total, setTotal] = useState(0);
  const [plannedCost, setPlannedCost] = useState(0);
  const [usedCost, setUsedCost] = useState(0);

  const [totalCost, setTotalCost] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [constructionType, setConstructionType] = useState(null);
  const [activeChart, setActiveChart] = useState("monthly");
  const [isChartExpanded, setIsChartExpanded] = useState(false);

  const monthlyData = useMemo(() => buildMonthlyData(data), [data]);
  const cumulativeData = useMemo(
    () => buildCumulativeData(monthlyData),
    [monthlyData]
  );

  const fetchData = async () => {
    setLoading(true);
    setErrMsg("");
    try {
      const res = await getCosts(projectId, constructionType, page, pageSize);
      setData(res.results || []);
      setTotalCost(res.total_cost ?? 0);
      setPlannedCost(res.planned_cost ?? 0);
      setUsedCost(res.used_cost ?? 0);
      setTotal(res.count || 0);
    } catch (err) {
      console.error("비용 목록 불러오기 실패:", err);
      setErrMsg("목록을 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [projectId, page, pageSize]);

  function buildMonthlyData(rawData, year = new Date().getFullYear()) {
    const grouped = {};
    rawData.forEach((row) => {
      if (!row.meta.payment_date) return;
      const date = new Date(row.meta.payment_date);
      const month = date.getMonth() + 1;
      const y = date.getFullYear();
      if (y !== year) return;
      if (!grouped[month]) {
        grouped[month] = { id: `${month}월`, 계획: 0, 실제: 0 };
      }
      grouped[month].계획 += Number(row.meta.expected_amount || 0);
      grouped[month].실제 += Number(row.meta.actual_amount || 0);
    });
    const fullYear = [];
    for (let m = 1; m <= 12; m++) {
      fullYear.push(grouped[m] || { id: `${m}월`, 계획: 0, 실제: 0 });
    }
    return fullYear;
  }

  function buildCumulativeData(monthlyData) {
    const cumulative = [];
    let sumPlan = 0;
    let sumActual = 0;
    monthlyData.forEach((row) => {
      sumPlan += row.계획 ?? 0;
      sumActual += row.실제 ?? 0;
      cumulative.push({ id: row.id, 계획: sumPlan, 실제: sumActual });
    });
    return cumulative;
  }

  const rows = useMemo(() => data ?? [], [data]);

  const formatAmount = (val) =>
    val != null ? `${Number(val).toLocaleString("ko-KR")}원` : "-";

  const handleRowClick = (costId) => setSelectedId(costId);

  const plannedPercent = totalCost
    ? ((plannedCost / totalCost) * 100).toFixed(1)
    : 0;
  const usedPercent = totalCost ? ((usedCost / totalCost) * 100).toFixed(1) : 0;

  const SkeletonCard = () => (
    <div className="bg-white rounded-lg p-4 animate-pulse border">
      <div className="flex justify-between items-start">
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-gray-200 rounded w-1/2" />
          <div className="h-3 bg-gray-100 rounded w-1/3" />
        </div>
        <div className="h-5 w-16 bg-gray-100 rounded" />
      </div>
    </div>
  );

  return (
    <div className="mobile-page-root mb-16">
      <div className="mobile-page-root-inner space-y-3">
        {/* 헤더 */}
        <div className="bg-white rounded-lg p-4 border min-w-0">
          <div className="flex items-center justify-between gap-2 mb-3 min-w-0">
            <h2 className="text-lg font-bold text-gray-900 truncate min-w-0 flex-1">
              기성 현황
            </h2>
            <button
              onClick={() => setIsModalOpen(true)}
              className="btn-create flex-shrink-0"
            >
              기성 등록
            </button>
          </div>

          <div className="text-2xl font-bold text-gray-900 mb-4">
            {Number(totalCost).toLocaleString("ko-KR")}원
          </div>

          {/* 계획/사용 요약 */}
          <div className="grid grid-cols-2 gap-3 min-w-0">
            <div className="bg-gray-50 rounded-lg p-3 border min-w-0">
              <div className="flex items-center gap-1.5 mb-1">
                <span className="text-xs text-gray-600">계획</span>
              </div>
              <div className="text-sm font-semibold text-gray-900">
                {Number(plannedCost).toLocaleString("ko-KR")}원
              </div>
              <div className="mt-1.5 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-600 rounded-full"
                  style={{ width: `${plannedPercent}%` }}
                />
              </div>
              <div className="text-[10px] text-gray-500 mt-1">
                {plannedPercent}%
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-3 border min-w-0">
              <div className="flex items-center gap-1.5 mb-1 min-w-0">
                <span className="text-xs text-gray-600">사용</span>
              </div>
              <div className="text-sm font-semibold text-gray-900">
                {Number(usedCost).toLocaleString("ko-KR")}원
              </div>
              <div className="mt-1.5 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-orange-500 rounded-full"
                  style={{ width: `${usedPercent}%` }}
                />
              </div>
              <div className="text-[10px] text-gray-500 mt-1">
                {usedPercent}%
              </div>
            </div>
          </div>
        </div>

        {errMsg && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-600">
            {errMsg}
          </div>
        )}

        {/* 차트 영역 */}
        <div className="bg-white rounded-lg border overflow-hidden shadow-sm min-w-0">
          {/* 헤더 */}
          <div className="px-4 py-3 border-b bg-white min-w-0">
            <div className="flex items-center justify-between gap-2 min-w-0">
              <div className="flex items-center gap-2">
                <div className="text-left">
                  <div className="text-sm font-semibold text-gray-900">
                    비용 차트
                  </div>
                  <div className="text-xs text-gray-500">월별/누적 분석</div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gray-50">
            {/* 차트 타입 선택 */}
            <div className="p-4 pb-3">
              <div className="flex bg-white rounded-lg p-1 border shadow-sm">
                <button
                  onClick={() => setActiveChart("monthly")}
                  className={`flex-1 py-2.5 text-xs font-semibold rounded-md transition-all ${activeChart === "monthly"
                    ? "bg-blue-600 text-white shadow-md"
                    : "text-gray-600 hover:bg-gray-50"
                    }`}
                >
                  월별
                </button>
                <button
                  onClick={() => setActiveChart("cumulative")}
                  className={`flex-1 py-2.5 text-xs font-semibold rounded-md transition-all ${activeChart === "cumulative"
                    ? "bg-blue-600 text-white shadow-md"
                    : "text-gray-600 hover:bg-gray-50"
                    }`}
                >
                  누적
                </button>
              </div>
            </div>

            {/* 범례 */}
            <div className="px-4 pb-3">
              <div className="flex items-center justify-center gap-4 bg-white rounded-lg p-2.5 border">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-sm bg-blue-500" />
                  <span className="text-xs text-gray-700 font-medium">
                    계획
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-sm bg-orange-500" />
                  <span className="text-xs text-gray-700 font-medium">
                    실제
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-0.5 bg-red-500" />
                  <span className="text-xs text-gray-700 font-medium">
                    라인
                  </span>
                </div>
              </div>
            </div>

            {/* 차트 */}
            <div className="px-4 pb-4">
              <div className="relative bg-white rounded-lg border overflow-hidden shadow-sm -mx-2">
                {/* 확대 버튼 */}
                <button
                  onClick={() => setIsChartExpanded(true)}
                  className="absolute top-3 right-3 z-10 w-9 h-9 bg-white/90 backdrop-blur-sm hover:bg-white rounded-lg shadow-lg border border-gray-200 flex items-center justify-center group transition-all active:scale-95"
                  title="차트 확대"
                >
                  <Maximize2 size={16} className="text-gray-700 group-hover:text-blue-600 transition-colors" />
                </button>

                {activeChart === "monthly" ? (
                  <NivoBar
                    title="월별 계획 vs 실제"
                    data={monthlyData}
                    keys={["계획", "실제"]}
                    indexBy="id"
                    height={320}
                    groupMode="grouped"
                    colors={({ id }) => (id === "계획" ? "#3B82F6" : "#F59E0B")}
                    showLine={true}
                    lineKeys={["계획", "실제"]}
                    lineColors={{ 계획: "#f50808ff", 실제: "#1900ffff" }}
                  />
                ) : (
                  <NivoBar
                    title="누적 계획 vs 실제"
                    data={cumulativeData}
                    keys={["계획", "실제"]}
                    indexBy="id"
                    height={320}
                    groupMode="grouped"
                    colors={({ id }) => (id === "계획" ? "#3B82F6" : "#F59E0B")}
                    showLine={true}
                    lineKeys={["계획", "실제"]}
                    lineColors={{ 계획: "#f50808ff", 실제: "#1900ffff" }}
                  />
                )}
              </div>
            </div>

            {/* 차트 인사이트 */}
            <div className="px-4 pb-4">
              <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                <div className="flex items-start gap-2">
                  <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-blue-600 text-xs">💡</span>
                  </div>
                  <div className="flex-1">
                    <div className="text-xs font-medium text-blue-900 mb-1">
                      {activeChart === "monthly" ? "월별 분석" : "누적 분석"}
                    </div>
                    <div className="text-xs text-blue-700 leading-relaxed">
                      {activeChart === "monthly"
                        ? "각 월의 계획 대비 실제 지출을 비교합니다. 막대는 금액, 라인은 누적 추이를 나타냅니다."
                        : "연간 누적 지출을 추적합니다. 계획 대비 실제 지출의 차이를 확인할 수 있습니다."}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 비용 리스트 */}
        <div className="space-y-2 min-w-0">
          <div className="flex items-center justify-between px-1 min-w-0">
            <span className="text-sm font-medium text-gray-700">지출 내역</span>
            <span className="text-xs text-gray-500">{total}건</span>
          </div>

          {loading ? (
            <>
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </>
          ) : rows.length === 0 ? (
            <div className="bg-white rounded-lg p-8 text-center border">
              <p className="text-sm text-gray-500">등록된 지출이 없습니다.</p>
              <button
                onClick={() => setIsModalOpen(true)}
                className="btn-create mt-4"
              >
                기성 등록
              </button>
            </div>
          ) : (
            rows.map((row) => {
              const expected = Number(row.meta.expected_amount) || 0;
              const actual = Number(row.meta.actual_amount) || 0;
              const diff = expected - actual;
              const rate =
                expected > 0 ? ((actual / expected) * 100).toFixed(1) : 0;

              return (
                <button
                  key={row.dns}
                  onClick={() => handleRowClick(row.dns)}
                  className="w-full text-left bg-white rounded-lg p-4 border hover:border-blue-300 active:bg-gray-50 transition-all overflow-hidden min-w-0"
                >
                  <div className="flex items-start justify-between gap-3 mb-3 min-w-0">
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <span className="inline-block px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-[10px] font-medium mb-1.5">
                        {row.meta.construction_type || "미분류"}
                      </span>
                      <div className="flex items-center gap-1.5 text-xs text-gray-500">
                        {row.meta.payment_date
                          ? formatDate(row.meta.payment_date)
                          : "-"}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-xs text-gray-400">
                        {row.meta.code || "-"}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2 text-center min-w-0">
                    <div className="bg-blue-50 rounded-lg py-2 min-w-0">
                      <div className="text-[10px] text-blue-600 mb-0.5">
                        계획
                      </div>
                      <div className="text-xs font-semibold text-blue-700">
                        {formatAmount(expected)}
                      </div>
                    </div>
                    <div className="bg-orange-50 rounded-lg py-2 min-w-0">
                      <div className="text-[10px] text-orange-600 mb-0.5">
                        실제
                      </div>
                      <div className="text-xs font-semibold text-orange-700">
                        {formatAmount(actual)}
                      </div>
                    </div>
                    <div
                      className={`rounded-lg py-2 min-w-0 ${diff > 0
                        ? "bg-red-50"
                        : diff < 0
                          ? "bg-green-50"
                          : "bg-gray-50"
                        }`}
                    >
                      <div
                        className={`text-[10px] mb-0.5 ${diff > 0
                          ? "text-red-600"
                          : diff < 0
                            ? "text-green-600"
                            : "text-gray-600"
                          }`}
                      >
                        집행률
                      </div>
                      <div
                        className={`text-xs font-semibold ${diff > 0
                          ? "text-red-700"
                          : diff < 0
                            ? "text-green-700"
                            : "text-gray-700"
                          }`}
                      >
                        {rate}%
                      </div>
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* 페이지네이션 */}
        {!loading && rows.length > 0 && (
          <div className="bg-white rounded-lg p-3 border min-w-0">
            <MobileBoardPagination
              total={total}
              page={page}
              pageSize={pageSize}
              onPageChange={(p) => setPage(p)}
            />
          </div>
        )}

        {/* 기성 등록 모달 */}
        <Cost_new
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          projectId={projectId}
          onCreated={fetchData}
        />

        {/* 상세 바텀시트 */}
        {selectedId && (
          <div
            className="fixed inset-0 z-50 bg-black/40"
            onClick={() => setSelectedId(null)}
          >
            <div
              className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl max-h-[85vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white pt-3 pb-2 px-4 border-b z-10">
                <div className="w-10 h-1 bg-gray-300 rounded-full mx-auto mb-3" />
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-semibold">비용 상세</h3>
                  <button
                    onClick={() => setSelectedId(null)}
                    className="px-3 py-1.5 text-sm text-gray-600 bg-gray-100 rounded-lg"
                  >
                    닫기
                  </button>
                </div>
              </div>
              <div className="p-4">
                <Cost_view
                  costId={selectedId}
                  onClose={() => setSelectedId(null)}
                  onUpdated={() => fetchData()}
                />
              </div>
            </div>
          </div>
        )}

        {/* 차트 확대 모달 */}
        {isChartExpanded && (
          <div
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4 animate-fadeIn"
            onClick={() => setIsChartExpanded(false)}
          >
            <div
              className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* 헤더 */}
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <h3 className="text-white font-semibold">
                    {activeChart === "monthly" ? "월별 분석" : "누적 분석"}
                  </h3>
                </div>
                <button
                  onClick={() => setIsChartExpanded(false)}
                  className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-colors text-white"
                >
                  닫기
                </button>
              </div>

              {/* 범례 */}
              <div className="bg-gray-50 px-4 py-3 border-b">
                <div className="flex items-center justify-center gap-6">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-blue-500" />
                    <span className="text-sm font-medium text-gray-700">
                      계획
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-orange-500" />
                    <span className="text-sm font-medium text-gray-700">
                      실제
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-1 bg-red-500" />
                    <span className="text-sm font-medium text-gray-700">
                      라인
                    </span>
                  </div>
                </div>
              </div>

              {/* 확대된 차트 - 스크롤 가능 */}
              <div className="bg-white p-4">
                {/* 스크롤 힌트 */}
                <div className="flex items-center justify-center gap-2 mb-3 text-xs text-gray-500">
                  <span>← 좌우로 스크롤하여 전체 차트 확인 →</span>
                </div>

                {/* 스크롤 영역 */}
                <div className="overflow-x-auto scrollbar-thin">
                  <div className="min-w-[900px]">
                    {activeChart === "monthly" ? (
                      <NivoBar
                        title=""
                        data={monthlyData}
                        keys={["계획", "실제"]}
                        indexBy="id"
                        height={500}
                        groupMode="grouped"
                        colors={({ id }) =>
                          id === "계획" ? "#3B82F6" : "#F59E0B"
                        }
                        showLine={true}
                        lineKeys={["계획", "실제"]}
                        lineColors={{ 계획: "#f50808ff", 실제: "#1900ffff" }}
                      />
                    ) : (
                      <NivoBar
                        title=""
                        data={cumulativeData}
                        keys={["계획", "실제"]}
                        indexBy="id"
                        height={500}
                        groupMode="grouped"
                        colors={({ id }) =>
                          id === "계획" ? "#3B82F6" : "#F59E0B"
                        }
                        showLine={true}
                        lineKeys={["계획", "실제"]}
                        lineColors={{ 계획: "#f50808ff", 실제: "#1900ffff" }}
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* 하단 힌트 */}
              <div className="bg-blue-50 px-4 py-3 border-t border-blue-100">
                <div className="flex items-center justify-center gap-2 text-xs text-blue-700">
                  <span>💡</span>
                  <span>
                    {activeChart === "monthly"
                      ? "각 월의 계획 대비 실제 지출을 확인할 수 있습니다"
                      : "연간 누적 계획 대비 실제 지출 추이를 확인할 수 있습니다"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cost;
